import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { PageInitService } from '../page-init-service';
import { PermissionService } from '@/domains/permission/services/permission-service';
import { TimeZoneUpdater } from '@/services/date-format/time-zone-service';
import { UnauthorizedHandler } from '@/services/unauthorized-handler/unauthorized-handler';
import { SharePageService } from '@/services/share-page/share-page-service';
import { PermissionV2Service, AddFreeService } from '@telecom/shared/domain/permission';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { create, type Deferred } from '@patsnap/tahiti-core';
import { GuestTaskMigrationService } from '@/domains/guest/services/guest-task-migration-service';
import { isFirstLogin } from '@telecom/shared/startup/user-auth';

@injectable()
export class PageInitServiceImpl implements PageInitService {
    public initDeferred: Deferred<void> = create<void>();
    // eslint-disable-next-line max-params
    constructor(
        @inject(PermissionService)
        private readonly _permissionService: PermissionService,
        @inject(TimeZoneUpdater)
        private readonly _timeZoneUpdater: TimeZoneUpdater,
        @inject(UnauthorizedHandler)
        private readonly _unauthorizedHandler: UnauthorizedHandler,
        @inject(SharePageService)
        private readonly _sharePageService: SharePageService,
        @inject(PermissionV2Service)
        private readonly _permissionV2Service: PermissionV2Service,
        @inject(AddFreeService)
        private readonly _addTrialService: AddFreeService,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(GuestTaskMigrationService)
        private readonly _guestTaskMigrationService: GuestTaskMigrationService,
    ) { }

    async pullPageInitData(): Promise<any> {
        this.initDeferred = create<void>();
        await Promise.all([this._tryPageInitRepository(), this._tryAddPlgFreePackage(), this._tryMigrateGuestTasks()]);
        await this.initDeferred.resolve();
    }

    private async _tryPageInitRepository() {
        const taskId = this._currentTaskService.taskId;
        const taskType = this._currentTaskService.taskType;
        await this._permissionV2Service.initialize({ taskId, taskType });

        await this._sharePageService.pageInitProxy()
            .then(res => {
                this._permissionService.updatePermission({
                    disclosure: res.permission.ai_disclosure,
                    translation: res.permission.ai_translation,
                    specificationCn: res.permission.ai_specification_cn,
                    specificationUs: res.permission.ai_specification_us,
                    specificationEu: res.permission.ai_specification_eu,
                    noveltyCheckReport: res.permission.ai_novelty_search,
                    ftoReport: res.permission.ai_fto_search,
                    ftoNew: res.permission.ai_fto_search,
                    workspacePermission: res.workspace_permission,
                    tierCode: res.tier_code,
                });
                this._timeZoneUpdater.updateTimeZone(res.time_zone.utc_offset);
            })
            .catch(err => {
                this._unauthorizedHandler.handle(err);
            });
    }

    /**
     * 初始化权限
     * 使用 AddTrialService 处理试用权限
     */
    private async _tryAddPlgFreePackage(): Promise<void> {
        try {
            const isFirst = await isFirstLogin();
            if (!isFirst) {
                return;
            }
            // 检查是否启用自动添加试用
            if (this._addTrialService.isAutoAddTrialEnabled()) {
                // 传递空的产品ID数组，让服务内部处理
                await this._addTrialService.addFreePkg([]);
            }
        } catch (error) {
            // 权限初始化失败不影响页面正常初始化
            console.warn('Permission initialization failed:', error);
        }
    }

    /**
     * 迁移游客任务到登录用户
     * 仅在用户首次登录时执行
     */
    private async _tryMigrateGuestTasks(): Promise<void> {
        try {
            const isFirst = await isFirstLogin();
            if (isFirst) {
                await this._guestTaskMigrationService.migrateTasksToUser();
            }
        } catch (error) {
            console.error('Failed to migrate guest tasks:', error);
        }
    }
}
